import { SimpleGrid, TextInput } from "@mantine/core";
import { Dispatch, SetStateAction, useState } from "react";
import { SectionCard } from "../HomeBlockShared";
import { DESIGN_IMAGE, HomeBlock } from "../homeBlockData";
import { IconLabel, ServiceConfig } from "../homeBlockConfig";
import {
  applyAction,
  Crop,
  EditableList,
  EditorLayout,
  fieldSetter,
  IconLabelList,
  iconLabelItem,
  ImagePicker,
  newId,
  ThumbPicker,
  useBlockConfig,
} from "./EditorKit";

const IMG = DESIGN_IMAGE.service;
const BACKGROUND_CROP: Crop = { x: 430, y: 0, w: 700, h: 230 };

const ROW1: [string, string, number, number][] = [
  ["Sửa Điện Thoại", "/sua-dien-thoai", 218, 132],
  ["Sửa Laptop", "/sua-laptop", 355, 132],
  ["Sửa Macbook", "/sua-macbook", 492, 140],
  ["Sửa Chữa Ipad", "/sua-ipad", 636, 140],
  ["Sửa AirPods", "/sua-airpods", 781, 142],
  ["Sửa Apple Watch", "/sua-apple-watch", 928, 152],
  ["Sửa Linh Kiện PC", "/sua-linh-kien-pc", 1085, 180],
];

const ROW2: [string, string, number, number][] = [
  ["Sửa Phím, Chuột, Tay Cầm", "/sua-phim-chuot", 15, 175],
  ["Sửa Màn Hình", "/sua-man-hinh", 195, 127],
  ["Sửa Loa", "/sua-loa", 327, 126],
  ["Sửa Máy Văn Phòng", "/sua-may-van-phong", 457, 125],
  ["Phụ Kiện Các Loại", "/phu-kien", 587, 130],
  ["Phục Vụ Tận Nơi", "/dich-vu-tan-noi", 722, 123],
  ["Phần Mềm Bản Quyền", "/phan-mem-ban-quyen", 849, 143],
  ["Gói Bảo Hành Mở Rộng", "/bao-hanh-mo-rong", 996, 144],
  ["Tin Công Nghệ", "/tin-cong-nghe", 1145, 120],
];

const SAMPLE_SERVICES = [
  ...ROW1.map(([label, link, x, w]) => ({ label, link, crop: { x, y: 232, w, h: 72 } })),
  ...ROW2.map(([label, link, x, w]) => ({ label, link, crop: { x, y: 310, w, h: 72 } })),
];

// Ảnh mẫu cắt từ thiết kế: chỉ để xem khi chưa tải ảnh, không lưu
const SAMPLE_CROPS = new Map<string, Crop>(
  SAMPLE_SERVICES.map((s, i): [string, Crop] => [`service-${i + 1}`, s.crop])
);

// Cập nhật một danh sách lồng trong config theo dạng setState
const listSetter =
  (set: Dispatch<SetStateAction<ServiceConfig>>, pick: "promises" | "highlights" | "steps"): Dispatch<SetStateAction<IconLabel[]>> =>
  (action) =>
    set((c) =>
      pick === "steps"
        ? { ...c, process: { ...c.process, steps: applyAction(action, c.process.steps) } }
        : { ...c, heading: { ...c.heading, [pick]: applyAction(action, c.heading[pick]) } }
    );

const initialConfig = (block: HomeBlock): ServiceConfig => {
  const sample = block.code === "home-service";
  return {
    heading: {
      title: sample ? "HACOM" : block.name,
      highlight: sample ? "DỊCH VỤ TỐT" : "",
      slogan: sample ? "CHUỖI SỬA CHỮA CHUYÊN SÂU TOÀN QUỐC" : "",
      subline: sample ? "LAPTOP - PC - ĐIỆN THOẠI - GAMING GEAR" : "",
      promises: sample
        ? [
            iconLabelItem("wrench", "Kỹ thuật viên chuyên sâu"),
            iconLabelItem("cpu", "Linh kiện chính hãng"),
            iconLabelItem("file-text", "Báo giá minh bạch"),
            iconLabelItem("store", "Hệ thống dịch vụ toàn quốc"),
          ]
        : [],
      highlights: sample
        ? [
            iconLabelItem("wrench", "Sửa chữa chuyên sâu"),
            iconLabelItem("shield-check", "Linh kiện chính hãng"),
            iconLabelItem("clock", "Xử lý nhanh chóng"),
            iconLabelItem("headset", "Tư vấn tận tâm"),
          ]
        : [],
      backgroundImage: null,
    },
    services: sample
      ? SAMPLE_SERVICES.map((s, i) => ({
          id: `service-${i + 1}`,
          label: s.label,
          link: s.link,
          image: null,
        }))
      : [],
    process: {
      label: sample ? "QUY TRÌNH DỊCH VỤ" : "",
      description: sample ? "ĐƠN GIẢN - MINH BẠCH - NHANH CHÓNG" : "",
      steps: sample
        ? [
            iconLabelItem("package", "Tiếp nhận thiết bị"),
            iconLabelItem("file-text", "Báo giá chi tiết"),
            iconLabelItem("wrench", "Sửa chữa / Xử lý"),
            iconLabelItem("shield-check", "Kiểm tra & bàn giao"),
          ]
        : [],
      buttonLabel: sample ? "Đăng ký dịch vụ ngay" : "",
      buttonLink: "",
    },
  };
};

const EditService = ({ block }: { block: HomeBlock }) => {
  const [config, setConfig] = useBlockConfig(block, () => initialConfig(block));
  const { heading, process } = config;
  // Ảnh nền mẫu cắt từ thiết kế: chỉ để xem, gỡ thì ẩn; không nằm trong config
  const [showSampleBackground, setShowSampleBackground] = useState(block.code === "home-service");

  const patchHeading = (v: Partial<ServiceConfig["heading"]>) =>
    setConfig((c) => ({ ...c, heading: { ...c.heading, ...v } }));
  const patchProcess = (v: Partial<ServiceConfig["process"]>) =>
    setConfig((c) => ({ ...c, process: { ...c.process, ...v } }));

  return (
    <EditorLayout block={block}>
      <SectionCard title="Đầu block">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
          <TextInput
            label="Tiêu đề"
            value={heading.title}
            onChange={(e) => patchHeading({ title: e.currentTarget.value })}
          />
          <TextInput
            label="Phần tô màu trong tiêu đề"
            value={heading.highlight}
            onChange={(e) => patchHeading({ highlight: e.currentTarget.value })}
          />
          <TextInput
            label="Khẩu hiệu"
            value={heading.slogan}
            onChange={(e) => patchHeading({ slogan: e.currentTarget.value })}
          />
          <TextInput
            label="Dòng phụ"
            value={heading.subline}
            onChange={(e) => patchHeading({ subline: e.currentTarget.value })}
          />
        </SimpleGrid>
        <IconLabelList
          label="Cam kết dưới tiêu đề"
          items={heading.promises}
          onChange={listSetter(setConfig, "promises")}
          max={4}
          addLabel="Thêm cam kết"
        />
        <IconLabelList
          label="Điểm nổi bật bên phải"
          items={heading.highlights}
          onChange={listSetter(setConfig, "highlights")}
          max={4}
          addLabel="Thêm điểm nổi bật"
        />
        <ImagePicker
          label="Ảnh nền"
          src={heading.backgroundImage}
          imageId={showSampleBackground ? IMG : undefined}
          crop={BACKGROUND_CROP}
          onPick={(url) => patchHeading({ backgroundImage: url })}
          onClear={() => {
            setShowSampleBackground(false);
            patchHeading({ backgroundImage: null });
          }}
          height={120}
        />
      </SectionCard>

      <SectionCard title="Danh mục dịch vụ">
        <EditableList
          items={config.services}
          onChange={fieldSetter(setConfig, "services")}
          itemLabel={(s) => s.label}
          columns={[
            { label: "Ảnh", width: "72px" },
            { label: "Nhãn", width: "minmax(0, 1fr)" },
            { label: "Link", width: "minmax(0, 1fr)" },
          ]}
          renderCells={(s, update) => (
            <>
              <ThumbPicker
                label={s.label}
                src={s.image}
                imageId={IMG}
                crop={SAMPLE_CROPS.get(s.id)}
                onPick={(url) => update({ image: url })}
                width={72}
              />
              <TextInput
                aria-label="Nhãn dịch vụ"
                value={s.label}
                onChange={(e) => update({ label: e.currentTarget.value })}
              />
              <TextInput
                aria-label={`Link ${s.label}`}
                value={s.link}
                onChange={(e) => update({ link: e.currentTarget.value })}
              />
            </>
          )}
          newItem={() => ({ id: newId(), label: "", link: "", image: null })}
          addLabel="Thêm dịch vụ"
          max={16}
          minWidth={520}
        />
      </SectionCard>

      <SectionCard title="Quy trình dịch vụ">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
          <TextInput
            label="Nhãn"
            value={process.label}
            onChange={(e) => patchProcess({ label: e.currentTarget.value })}
          />
          <TextInput
            label="Mô tả"
            value={process.description}
            onChange={(e) => patchProcess({ description: e.currentTarget.value })}
          />
        </SimpleGrid>
        <IconLabelList
          label="Các bước"
          textLabel="Tên bước"
          items={process.steps}
          onChange={listSetter(setConfig, "steps")}
          max={5}
          addLabel="Thêm bước"
        />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
          <TextInput
            label="Nhãn nút"
            value={process.buttonLabel}
            onChange={(e) => patchProcess({ buttonLabel: e.currentTarget.value })}
          />
          <TextInput
            label="Link nút"
            description="Trang đăng ký dịch vụ khi bấm nút."
            inputWrapperOrder={["label", "input", "description"]}
            value={process.buttonLink}
            onChange={(e) => patchProcess({ buttonLink: e.currentTarget.value })}
          />
        </SimpleGrid>
      </SectionCard>
    </EditorLayout>
  );
};

export default EditService;
